import { useRef, useEffect, useState, useCallback } from 'react';

const GRID = 20;
const CELL = 18; // canvas = 360 x 360
const SPEED = 110; // ms per step

function spawnFood(snake) {
  let food;
  do {
    food = { x: Math.floor(Math.random() * GRID), y: Math.floor(Math.random() * GRID) };
  } while (snake.some((s) => s.x === food.x && s.y === food.y));
  return food;
}

export default function SnakeGame({ onScore }) {
  const canvasRef = useRef();
  const stateRef = useRef();
  const loopRef = useRef();
  const [score, setScore] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | playing | over

  const draw = useCallback(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;
    const s = stateRef.current;
    ctx.fillStyle = '#080f1a';
    ctx.fillRect(0, 0, GRID * CELL, GRID * CELL);

    // food
    ctx.font = `${CELL}px serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('🍎', s.food.x * CELL + CELL / 2, s.food.y * CELL + CELL / 2 + 1);

    // snake
    s.snake.forEach((seg, i) => {
      ctx.fillStyle = i === 0 ? '#c9a84c' : '#3b6ea5';
      ctx.fillRect(seg.x * CELL + 1, seg.y * CELL + 1, CELL - 2, CELL - 2);
    });
  }, []);

  const step = useCallback(() => {
    const s = stateRef.current;
    s.dir = s.nextDir;
    if (s.dir.x === 0 && s.dir.y === 0) return; // not moving yet
    const head = { x: s.snake[0].x + s.dir.x, y: s.snake[0].y + s.dir.y };

    // wall or self collision
    if (
      head.x < 0 || head.x >= GRID || head.y < 0 || head.y >= GRID ||
      s.snake.some((seg) => seg.x === head.x && seg.y === head.y)
    ) {
      clearInterval(loopRef.current);
      setStatus('over');
      onScore(s.score);
      return;
    }

    s.snake.unshift(head);
    if (head.x === s.food.x && head.y === s.food.y) {
      s.score += 1;
      setScore(s.score);
      s.food = spawnFood(s.snake);
    } else {
      s.snake.pop();
    }
    draw();
  }, [draw, onScore]);

  const start = useCallback(() => {
    stateRef.current = {
      snake: [{ x: 10, y: 10 }, { x: 9, y: 10 }, { x: 8, y: 10 }],
      dir: { x: 0, y: 0 },
      nextDir: { x: 1, y: 0 },
      food: spawnFood([{ x: 10, y: 10 }]),
      score: 0,
    };
    setScore(0);
    setStatus('playing');
    draw();
    clearInterval(loopRef.current);
    loopRef.current = setInterval(step, SPEED);
  }, [draw, step]);

  useEffect(() => {
    function onKey(e) {
      const s = stateRef.current;
      if (!s || status !== 'playing') return;
      const d = s.dir;
      const map = {
        ArrowUp: { x: 0, y: -1 }, w: { x: 0, y: -1 },
        ArrowDown: { x: 0, y: 1 }, s: { x: 0, y: 1 },
        ArrowLeft: { x: -1, y: 0 }, a: { x: -1, y: 0 },
        ArrowRight: { x: 1, y: 0 }, d: { x: 1, y: 0 },
      };
      const nd = map[e.key];
      if (!nd) return;
      e.preventDefault();
      // can't reverse directly
      if (nd.x === -d.x && nd.y === -d.y) return;
      s.nextDir = nd;
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [status]);

  useEffect(() => () => clearInterval(loopRef.current), []);

  return (
    <div className="flex flex-col items-center gap-3">
      <div className="flex items-center justify-between w-full">
        <span className="text-gold-500 font-black text-lg">🐍 {score}</span>
        <span className="text-gray-500 text-xs">Arrow keys / WASD</span>
      </div>
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={GRID * CELL}
          height={GRID * CELL}
          className="rounded-lg border"
          style={{ borderColor: '#1e3a5f' }}
        />
        {status !== 'playing' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-lg" style={{ backgroundColor: 'rgba(8,15,26,0.85)' }}>
            {status === 'over' && <p className="text-red-400 font-black text-xl">GAME OVER 💀</p>}
            {status === 'over' && <p className="text-gray-300 text-sm">You scored {score}. Was it worth it?</p>}
            <button
              onClick={start}
              className="px-5 py-2 rounded font-bold uppercase tracking-widest text-sm"
              style={{ backgroundColor: '#c9a84c', color: '#0d1b2e' }}
            >
              {status === 'idle' ? '▶ Play Snake' : '↻ Again'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
